import { RollupApi } from '@business/api/tm_rollup';
import { RollupId, TmRollupResponse } from '@business/entities/tm/TmRollupResponse';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { askChangeReason } from '@modules/common/ChangeReasonModal';
import { switchMap, tap } from 'rxjs/operators';

export class RollupBloc extends BaseBloc {
  private readonly tmRollupsRepo = new Repository<TmRollupResponse>({
    getItemId: term => term.id,
  });

  readonly rollups$ = this.tmRollupsRepo.items$;
  readonly selectTemplate = this.tmRollupsRepo.selectItem;

  onReset() {
    this.tmRollupsRepo.reset();
  }

  fetchRollups = () => {
    return RollupApi.fetchRollups().pipe(this.tmRollupsRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_region_error') }));
  };

  fetchRollup = (id: RollupId) => {
    return RollupApi.fetchRollup(id).pipe(
      this.tmRollupsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_region_error') }),
    );
  };

  createRollup = (data: Partial<TmRollupResponse>) => {
    return RollupApi.createRollup(data).pipe(
      this.tmRollupsRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_region'),
      }),
    );
  };

  // Edits and deletes ask for the reason first (askChangeReason, kept in the Activity Log); closing that window saves
  // nothing
  updateRollup = (id: RollupId, data: Partial<TmRollupResponse>) => {
    return askChangeReason().pipe(
      switchMap(changeNote => RollupApi.updateRollup(id, data, changeNote)),
      this.tmRollupsRepo.ops.upsertOne(item => ({ item })),
      tap(() => this.fetchRollups().subscribe()),
      handleMessage({
        type: requestMessage('update_region'),
      }),
    );
  };

  deleteRollup = (id: RollupId) => {
    return askChangeReason('Delete Rollup', 'Do you really want to remove this rollup?', 'Delete').pipe(
      switchMap(changeNote => RollupApi.deleteRollup(id, changeNote)),
      this.tmRollupsRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_region'),
      }),
    );
  };
}
